import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SystemId } from "@/components/ui/system-id";
import {
  type MilestoneAnalysis,
  useUpdateMilestoneAnalysisStatus,
} from "@/hooks/api/use-milestone-analyses";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";

interface HistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sortedAnalyses: MilestoneAnalysis[];
  onAnalysisChange?: (analysis: MilestoneAnalysis) => void;
  onViewAnalysis?: (analysis: MilestoneAnalysis) => void;
}

type ActionType = "approve" | "unapprove" | "publish" | "unpublish";

export function HistoryDialog({
  open,
  onOpenChange,
  sortedAnalyses,
  onAnalysisChange,
  onViewAnalysis,
}: HistoryDialogProps) {
  const updateStatusMutation = useUpdateMilestoneAnalysisStatus();
  const [pendingAction, setPendingAction] = useState<{
    analysisId: string;
    action: ActionType;
  } | null>(null);

  const isLoading = (analysisId: string, action: ActionType) => {
    return (
      updateStatusMutation.isPending &&
      pendingAction?.analysisId === analysisId &&
      pendingAction?.action === action
    );
  };

  const handlePublish = (analysisId: string) => {
    setPendingAction({ analysisId, action: "publish" });
    updateStatusMutation.mutate(
      { analysisId, isActiveVersion: true, isApproved: true },
      {
        onSuccess: () => {
          setPendingAction(null);
          onOpenChange(false);
          const publishedAnalysis = sortedAnalyses.find(
            (a) => a.analysis_id === analysisId,
          );
          if (publishedAnalysis && onAnalysisChange) {
            onAnalysisChange(publishedAnalysis);
          }
        },
        onError: (error) => {
          console.error("Error publishing analysis:", error);
          setPendingAction(null);
        },
      },
    );
  };

  const handleUnpublish = (analysisId: string) => {
    setPendingAction({ analysisId, action: "unpublish" });
    updateStatusMutation.mutate(
      { analysisId, isActiveVersion: false, isApproved: true },
      {
        onSuccess: () => {
          setPendingAction(null);
          // Cache is updated automatically by the mutation
        },
        onError: (error) => {
          console.error("Error unpublishing analysis:", error);
          setPendingAction(null);
        },
      },
    );
  };

  const handleView = (analysis: MilestoneAnalysis) => {
    if (onAnalysisChange) {
      onAnalysisChange(analysis);
    }
    if (onViewAnalysis) {
      onViewAnalysis(analysis);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col overflow-hidden bg-card">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Analysis History</DialogTitle>
        </DialogHeader>
        <div className="flex-grow overflow-y-auto">
          <div className="space-y-4 px-1 py-4">
            {sortedAnalyses.map((analysis) => (
              <div
                key={analysis.analysis_id}
                className={cn(
                  "p-4 rounded-md border transition-colors",
                  analysis.is_active_version ? "border-primary" : "",
                )}
              >
                <div className="flex justify-between mb-2">
                  <div className="flex flex-col gap-2 max-w-[95%]">
                    <div className="text-sm">{analysis.opinion?.opinion}</div>
                  </div>
                  <span className="text-xs text-muted-foreground ml-4 whitespace-nowrap">
                    {formatDate(analysis.last_updated || analysis.created)}
                  </span>
                </div>
                <div>
                  {analysis.extra_instructions && (
                    <p className="text-sm mb-4 text-muted-foreground">
                      Instructions: {analysis.extra_instructions}
                    </p>
                  )}
                </div>
                <div className="flex justify-between items-center mt-2">
                  <div className="text-sm text-muted-foreground">
                    <SystemId id={analysis.analysis_id} />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleView(analysis)}
                    >
                      View
                    </Button>

                    {analysis.is_active_version ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnpublish(analysis.analysis_id)}
                        disabled={updateStatusMutation.isPending}
                      >
                        {isLoading(analysis.analysis_id, "unpublish") ? (
                          <div className="flex items-center">
                            <Loader2 className="h-3 w-3 animate-spin mr-2" />
                            Unpublishing...
                          </div>
                        ) : (
                          "Unpublish"
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handlePublish(analysis.analysis_id)}
                        disabled={updateStatusMutation.isPending}
                      >
                        {isLoading(analysis.analysis_id, "publish") ? (
                          <div className="flex items-center">
                            <Loader2 className="h-3 w-3 animate-spin mr-2" />
                            Publishing...
                          </div>
                        ) : (
                          "Publish"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
